import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { z } from "zod";
import { differenceInYears, formatISODuration, intervalToDuration, isFuture, isPast, isToday, isValid, parse, } from "date-fns";
import { IdentificationMode, PaymentType } from "@/pages/CheckoutPage/types";
import { ACTIONS } from "@/utils/context/ModalContext";
import { CheckoutSectionText } from "@/pages/CheckoutPage/components";
import { THEME } from "@core/theme";
import styled from "styled-components";
import { provideIsLoadingProp, } from "@/utils/context/LoadingContext";
import { Headline } from "@mm/ui";
import { extendURLParams } from "@/utils/fetchData";
// TODO: refactor to its own atom component
const { skeletonBase } = THEME;
const CheckoutButton = styled.button.attrs(provideIsLoadingProp) `
  background: none;
  border: 0;
  outline: 0;
  padding: 0;
  font-size: 1rem;
  text-decoration: underline;
  color: ${({ theme }) => theme.colors.grey[4]};
  cursor: pointer;

  ${({ isLoading }) => isLoading && skeletonBase + "height: 0.875rem;"};
`;
const Image = styled.img `
  max-width: 100%;
  object-fit: contain;
  display: block;
  margin: 4rem auto;
`;
const identificationSectionBaseSchema = () => ({
    // TODO: refactor to general birthday rule that will be used throughout all
    // sections, schemas and configs
    identificationBirthday: z
        .string({
        required_error: "Bitte gib dein Geburtsdatum ein.",
    })
        .trim()
        .nonempty("Bitte gib dein Geburtsdatum ein.")
        .regex(/^[0-9]{2}\.[0-9]{2}\.[0-9]{4}$/, "Bitte gib ein Datum im Format 'TT.MM.JJJJ' ein.")
        .refine((value) => {
        const startDate = parse(value, "dd.MM.yyyy", new Date());
        return isValid(startDate);
    }, {
        message: "Bitte gib ein gültiges Datum ein.",
    })
        .refine((value) => {
        try {
            const startDate = parse(value, "dd.MM.yyyy", new Date());
            if (!isValid(startDate) || isFuture(startDate))
                return false;
            const age = formatISODuration(intervalToDuration({
                start: parse(value, "dd.MM.yyyy", new Date()),
                end: new Date(),
            })).match(/^P(\d+)Y/)?.[1];
            return Number(age) >= 18;
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            return false;
        }
    }, {
        message: "Das Mindestalter zum Abschluss des Vertrages beträgt 18 Jahre.",
    })
        .refine((value) => {
        try {
            const startDate = parse(value, "dd.MM.yyyy", new Date());
            if (!isValid(startDate) || isFuture(startDate))
                return false;
            const age = formatISODuration(intervalToDuration({
                start: parse(value, "dd.MM.yyyy", new Date()),
                end: new Date(),
            })).match(/^P(\d+)Y/)?.[1];
            return Number(age) <= 90;
        }
        catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            return false;
        }
    }, {
        message: "Für den Vertragsabschluss darfst du höchstens 90 Jahre alt sein.",
    }),
    identificationBirthplace: z
        .string({
        required_error: "Bitte gib einen Geburtsort an.",
    })
        .trim()
        .nonempty("Bitte gib einen Geburtsort an."),
    nationality: z
        .string({
        required_error: "Bitte gib deine Nationalität ein.",
    })
        .trim()
        .nonempty("Bitte gib deine Nationalität ein."),
});
const documentExpirationDateSchema = z
    .string({
    required_error: "Bitte gib ein Ablaufdatum des Dokuments an.",
})
    .trim()
    .nonempty("Bitte gib ein Ablaufdatum des Dokuments an.")
    .regex(/^[0-9]{2}\.[0-9]{2}\.[0-9]{4}$/, "Bitte gib ein Datum im Format 'TT.MM.JJJJ' ein.")
    .refine((value) => {
    const startDate = parse(value, "dd.MM.yyyy", new Date());
    return isValid(startDate);
}, {
    message: "Bitte gib ein gültiges Datum ein.",
})
    .refine((value) => {
    try {
        const startDate = parse(value, "dd.MM.yyyy", new Date());
        if (!isValid(startDate) || isToday(startDate) || isPast(startDate)) {
            return false;
        }
        return true;
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return false;
    }
}, {
    message: "Dein Prüfdokument muss noch gültig sein.",
})
    .refine((value) => {
    try {
        const startDate = parse(value, "dd.MM.yyyy", new Date());
        if (!isValid(startDate) || isToday(startDate) || isPast(startDate)) {
            return false;
        }
        const diff = differenceInYears(parse(value, "dd.MM.yyyy", new Date()), new Date());
        return diff < 20;
    }
    catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return false;
    }
}, {
    message: "Dein Prüfdokument kann nicht mehr als 20 Jahre gültig sein.",
});
const documentNumberSchema = z
    .string({
    required_error: "Bitte gib eine Ausweisnummer an.",
})
    .trim()
    .nonempty("Bitte gib eine Ausweisnummer an.");
export const config = [
    {
        key: "letsGoMobile",
        steps: [
            {
                type: "contactAndPayment",
                sections: [
                    {
                        title: "Kontakt & Bezahlung",
                        sections: [
                            {
                                component: "contactAddressSection",
                                defaultValues: {
                                    contactAddressSalutation: "Frau",
                                },
                                options: {
                                    introText: "Bei der Bestellung eines Mobilfunk-Vertrags ist die Prüfung deiner Identität notwendig. Damit hier alles reibungslos klappt, gib bitte deine Meldeadresse und alle Vor- und Nachnamen an, die auf deinem Ausweis stehen.",
                                    hideContactAddressBirthday: true,
                                    hideContactAddressPhoneNumber: true,
                                    showContactAddressAddendum: true,
                                    showContactAddressMobilePhoneNumber: true,
                                    enableAddressSuggestions: true,
                                },
                                schema: z.object({
                                    contactAddressSalutation: z.enum(["Herr", "Frau"], {
                                        required_error: "Bitte wähle eine Anrede aus.",
                                    }),
                                    contactAddressFirstName: z
                                        .string({
                                        required_error: "Bitte gib deinen Vornamen an.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deinen Vornamen ein."),
                                    contactAddressLastName: z
                                        .string({
                                        required_error: "Bitte gib deinen Nachnamen an.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deinen Nachnamen ein."),
                                    contactAddressZipCode: z
                                        .string({
                                        required_error: "Bitte gib deine Postleitzahl ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine Postleitzahl ein.")
                                        .length(5, "Deine Postleitzahl muss aus 5 Ziffern bestehen.")
                                        .regex(/^[0-9]+$/, "Deine Postleitzahl muss aus Zahlen bestehen."),
                                    contactAddressPlace: z
                                        .string({
                                        required_error: "Bitte wähle deinen Wohnort aus.",
                                    })
                                        .trim()
                                        .nonempty("Bitte wähle deinen Wohnort aus."),
                                    contactAddressStreet: z
                                        .string({
                                        required_error: "Bitte gib deinen Straßennamen ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deinen Straßennamen ein.")
                                        .max(50, "Dein Straßenname darf maximal 50 Zeichen lang sein."),
                                    contactAddressStreetNumber: z
                                        .string({
                                        required_error: "Bitte gib deine Hausnummer ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine Hausnummer ein.")
                                        .regex(/^[1-9][0-9]*[a-z]?(\s*(\/|-)\s*[1-9][0-9]*[a-z])?$/, "Bitte prüfe die Hausnummer."),
                                    contactAddressAddendum: z.string().trim().optional(),
                                    contactAddressMobilePhoneNumber: z
                                        .string({
                                        required_error: "Bitte gib deine Handynummer ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine Handynummer ein.")
                                        .regex(/^(\+49|0049|0)1[5-7][0-9]{6,9}$/, "Bitte gib deine Handynummer im folgenden Format ein: 017612345678."),
                                    contactAddressEmail: z
                                        .string({
                                        required_error: "Bitte gib deine E-Mail Adresse ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine E-Mail Adresse ein.")
                                        .email("Bitte gib eine gültige E-Mail-Adresse ein."),
                                    contactAddressConfirmEmail: z
                                        .string({
                                        required_error: "Bitte gib deine E-Mail Adresse erneut ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine E-Mail Adresse erneut ein.")
                                        .email("Bitte gib eine gültige E-Mail-Adresse ein."),
                                }),
                            },
                        ],
                    },
                    {
                        sections: [
                            {
                                component: "newsletterSection",
                                defaultValues: {
                                    optinNewsletter: false,
                                },
                                options: {
                                    label: (_jsx(_Fragment, { children: _jsxs("p", { children: ["Ja, ich m\u00F6chte keine attraktiven Angebote verpassen! Ich bin einverstanden, dass mich die S-KON eKontor 24 GmbH per E-Mail, SMS oder Telefon \u00FCber aktuelle Aktionen, exklusive Deals und relevante Services informiert. Diese Einwilligung kann ich jederzeit mit Wirkung f\u00FCr die Zukunft widerrufen. Weitere Informationen finde ich in der", " ", _jsx("a", { href: "/datenschutz/", target: "_blank", rel: "noreferrer", "aria-label": "Datenschuterkl\u00E4rung \u00F6ffnet in neuem Tab", children: "Datenschutzerkl\u00E4rung." })] }) })),
                                },
                                schema: z.object({
                                    optinNewsletter: z.boolean(),
                                }),
                            },
                        ],
                    },
                    {
                        sections: [
                            {
                                component: "bankAccountSection",
                                defaultValues: {
                                    sepaAccepted: false,
                                },
                                options: {
                                    label: "Ich ermächtige die Telefónica Germany GmbH & Co. OHG, Zahlungen von meinem Konto per SEPA Lastschrift einzuziehen.",
                                },
                                schema: z.object({
                                    sepaAccepted: z.boolean().refine((value) => value, {
                                        message: "Bitte stimme dem SEPA-Mandat zu",
                                    }),
                                    iban: z
                                        .string({
                                        required_error: "Bitte gib deine IBAN ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine IBAN ein."),
                                }),
                            },
                            {
                                component: "deliveryPaymentSection",
                                hide: (options, state, data) => {
                                    return Boolean(!data?.summary?.paymentTypes ||
                                        data?.summary.paymentTypes.length === 0 ||
                                        data?.summary?.paymentTypes?.includes(PaymentType.NoPayment));
                                },
                                defaultValues: {
                                    paymentType: PaymentType.Paypal,
                                },
                                options: {
                                    title: "Zahlung für den Versand",
                                },
                                schema: z.object({
                                    paymentType: z.nativeEnum(PaymentType),
                                }),
                            },
                        ],
                    },
                    {
                        component: "o2.contractSummaryApprovalSection",
                    },
                ],
            },
            {
                type: "contractSummary",
                sections: [
                    {
                        title: "Tarif",
                        sections: [
                            {
                                component: "o2.numberPortabilityInfoSection",
                            },
                            {
                                component: "o2.eSimSection",
                            },
                            {
                                component: "o2.personalCustomerCodeSection",
                                schema: z.object({
                                    personalCustomerCode: z
                                        .string({
                                        required_error: "Bitte gib deine Persönliche Kundenkennzahl ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib eine Persönliche Kundenkennzahl ein.")
                                        .regex(/^[0-9]+$/, "Deine Kundenkennzahl muss aus Zahlen bestehen.")
                                        .length(4, "Bitte gib eine 4-stellige Kundenkennzahl ein."),
                                }),
                            },
                            {
                                component: "identificationSection",
                                options: {
                                    introText: "Für deinen Tarif benötigen wir deine Ausweisdaten zur Weiterleitung an o2.",
                                },
                                defaultValues: {
                                    identificationMode: IdentificationMode.idCard,
                                },
                                schema: z.union([
                                    z.object({
                                        identificationMode: z.literal(IdentificationMode.idCard, {
                                            required_error: "Bitte triff eine Auswahl.",
                                        }),
                                        ...identificationSectionBaseSchema(),
                                        idCardExpirationDate: documentExpirationDateSchema,
                                        idCardNumber: documentNumberSchema,
                                    }),
                                    z.object({
                                        identificationMode: z.literal(IdentificationMode.passport, {
                                            required_error: "Bitte triff eine Auswahl.",
                                        }),
                                        ...identificationSectionBaseSchema(),
                                        passportExpirationDate: documentExpirationDateSchema,
                                        passportNumber: documentNumberSchema.regex(/^[a-zA-Z0-9 ]{6,30}$/, "Bitte gib eine Ausweisnummer an."),
                                    }),
                                ]),
                            },
                            {
                                component: "webIdentSection",
                                options: {
                                    content: (dispatchModal) => {
                                        const { cdnUrl } = extendURLParams();
                                        return (_jsxs(_Fragment, { children: [_jsx(CheckoutSectionText, { children: "Seit dem 01. Juli 2017 fordert der Gesetzgeber vor Aktivierung der SIM-Karte eine Identifizierung des K\u00E4ufers. Hierf\u00FCr bieten wir dir das bequeme Video-Verfahren \u00FCber unseren Partner WebID an." }), _jsx(CheckoutSectionText, { children: "Die Identifikation gelingt per Video-Chat ganz einfach. Du kannst sie gleich im Anschluss an die Bestellung durchf\u00FChren. Das geht von Zuhause und unterwegs. Alles, was du ben\u00F6tigst ist" }), _jsxs("ul", { children: [_jsx("li", { children: "eine Kamera an deinem Smartphone, PC oder Tablet" }), _jsx("li", { children: "deinen Personalausweis oder ein anderes g\u00FCltiges Ausweisdokument mit Lichtbild" })] }), _jsx(CheckoutButton, { role: "dialog", "aria-modal": "true", "aria-label": "weitere Informationen", onClick: (e) => {
                                                        e.preventDefault();
                                                        dispatchModal({
                                                            type: ACTIONS.SET_MODAL,
                                                            payload: {
                                                                type: "generic",
                                                                title: "So funktioniert deine Online-Identifikation",
                                                                content: (_jsxs(_Fragment, { children: [_jsx(Headline, { variant: "h2", children: "So funktioniert deine Online-Identifikation" }), _jsx("p", { children: "Starte den Identifikationsprozess direkt im Anschluss an deine Bestellung \u00FCber den entsprechenden Link auf der Best\u00E4tigungsseite. Du hast gerade keine Zeit oder die ben\u00F6tigte Ausstattung nicht zur Hand? Den f\u00FCr die Identifikation ben\u00F6tigten Link bekommst du zus\u00E4tzlich noch einmal per Mail zugesendet. So kannst du ebenfalls schnell und unkompliziert innerhalb von f\u00FCnf Kalendertagen die Identifizierung vornehmen." }), _jsx(Image, { src: `${cdnUrl}/img/webid/icon-webid-progress.png`, alt: "Fortschritt Web ID" }), _jsx("h3", { children: "Das ben\u00F6tigst du f\u00FCr eine Online-Identifikation." }), _jsxs("ul", { children: [_jsx("li", { children: "Eine funktionsf\u00E4hige Kamera an deinem PC, Tablet oder Smartphone" }), _jsx("li", { children: "Eine stabile Internetverbindung" }), _jsx("li", { children: "Ein g\u00FCltiges Ausweisdokument, wie z. B. Personalausweis oder Reisepass" })] })] })),
                                                            },
                                                        });
                                                    }, children: "weitere Informationen" })] }));
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
            {
                type: "orderSummary",
                showSummaryOnMobile: true,
                submitLabel: "Kostenpflichtig bestellen",
                // TODO: temporary; remove after testing
                sections: [
                    {
                        title: "Kontakt & Bezahlung",
                        sections: [
                            {
                                component: "contactAndPaymentOverviewSection",
                                options: {
                                    hideShippingAddress: true,
                                },
                            },
                        ],
                    },
                    {
                        title: "Tarif",
                        sections: [
                            {
                                component: "o2.simOnly.tariffOverviewSection",
                            },
                        ],
                    },
                    {
                        title: "Rechtliche Hinweise",
                        sections: [
                            {
                                component: "legalNoteSection",
                                options: {
                                    legalNote: () => (_jsxs(_Fragment, { children: [_jsx("p", { children: "Mit Klick auf \u201CKostenpflichtig bestellen\u201D erkl\u00E4rst du, dass du" }), _jsxs("ul", { children: [_jsxs("li", { children: ["die", " ", _jsx("a", { href: "/agb/", target: "_blank", rel: "noreferrer", children: "Allgemeinen Gesch\u00E4ftsbedingungen der S-KON eKontor 24 GmbH" })] }), _jsxs("li", { children: ["die", " ", _jsx("a", { href: "/agb-telefonica/", target: "_blank", rel: "noreferrer", children: "Allgemeinen Gesch\u00E4ftsbedingungen der Telef\u00F3nica Germany GmbH & Co. OHG" })] }), _jsxs("li", { children: ["die", " ", _jsx("a", { href: "https://www.webid-solutions.de/de/terms-and-conditions/", target: "_blank", rel: "noreferrer", children: "Allgemeinen Gesch\u00E4ftsbedingungen der WebID Solutions GmbH" })] }), _jsxs("li", { children: ["die", " ", _jsx("a", { href: "https://tarife.mediamarkt.de/widerrufsrecht/", target: "_blank", rel: "noreferrer", children: "Widerrufsbelehrung / das Muster-Widerrufsformular" })] })] }), _jsx("p", { children: "zur Kenntnis genommen hast und mit ihnen einverstanden bist." }), _jsx("p", { children: "Zudem erkl\u00E4rst du deine Einwilligung (hier ansehen) in die Speicherung deiner personenbezogenen Daten durch die WebID Solutions GmbH im Nutzerprofil und in die Verarbeitung der Daten f\u00FCr k\u00FCnftige Identifikationen bzw. digitale Signaturen. Deine Einwilligung kannst du im Videocall oder durch E-Mail an datenschutz@webid-solutions.de widerrufen." }), _jsxs("p", { children: ["Informationen zur Verarbeitung deiner personenbezogenen Daten, insbesondere der Identifizierung \u00FCber die WebID Solutions und zur Bestellabwicklung findest du in den Informationen zum", " ", _jsx("a", { href: "/datenschutz/", target: "_blank", rel: "noreferrer", children: "Datenschutz der S-KON ekontor 24 GmbH" }), " ", "und Informationen zum", " ", _jsx("a", { href: "https://content.ekontor24.net/mediamarkt/pdf/datenschutzmerkblatt-telefonica.pdf", target: "_blank", rel: "noreferrer", children: "Datenschutz von Telef\u00F3nica Germany GmbH & Co. OHG" }), "."] })] })),
                                },
                            },
                            {
                                component: "o2.contractSummarySection",
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        key: "o2",
        steps: [
            {
                type: "contactAndPayment",
                sections: [
                    {
                        title: "Kontakt & Bezahlung",
                        sections: [
                            {
                                component: "contactAddressSection",
                                defaultValues: {
                                    contactAddressSalutation: "Frau",
                                },
                                options: {
                                    introText: "Bei der Bestellung eines Mobilfunk-Vertrags ist die Prüfung deiner Identität notwendig. Damit hier alles reibungslos klappt, gib bitte deine Meldeadresse und alle Vor- und Nachnamen an, die auf deinem Ausweis stehen.",
                                    hideContactAddressBirthday: true,
                                    hideContactAddressPhoneNumber: true,
                                    showContactAddressAddendum: true,
                                    showContactAddressMobilePhoneNumber: true,
                                    enableAddressSuggestions: true,
                                },
                                schema: z.object({
                                    contactAddressSalutation: z.enum(["Herr", "Frau"], {
                                        required_error: "Bitte wähle eine Anrede aus.",
                                    }),
                                    contactAddressFirstName: z
                                        .string({
                                        required_error: "Bitte gib deinen Vornamen an.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deinen Vornamen ein."),
                                    contactAddressLastName: z
                                        .string({
                                        required_error: "Bitte gib deinen Nachnamen an.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deinen Nachnamen ein."),
                                    contactAddressZipCode: z
                                        .string({
                                        required_error: "Bitte gib deine Postleitzahl ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine Postleitzahl ein.")
                                        .length(5, "Deine Postleitzahl muss aus 5 Ziffern bestehen.")
                                        .regex(/^[0-9]+$/, "Deine Postleitzahl muss aus Zahlen bestehen."),
                                    contactAddressPlace: z
                                        .string({
                                        required_error: "Bitte wähle deinen Wohnort aus.",
                                    })
                                        .trim()
                                        .nonempty("Bitte wähle deinen Wohnort aus."),
                                    contactAddressStreet: z
                                        .string({
                                        required_error: "Bitte gib deinen Straßennamen ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deinen Straßennamen ein.")
                                        .max(50, "Dein Straßenname darf maximal 50 Zeichen lang sein."),
                                    contactAddressStreetNumber: z
                                        .string({
                                        required_error: "Bitte gib deine Hausnummer ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine Hausnummer ein.")
                                        .regex(/^[1-9][0-9]*[a-z]?(\s*(\/|-)\s*[1-9][0-9]*[a-z])?$/, "Bitte prüfe die Hausnummer."),
                                    contactAddressAddendum: z.string().trim().optional(),
                                    contactAddressMobilePhoneNumber: z
                                        .string({
                                        required_error: "Bitte gib deine Handynummer ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine Handynummer ein.")
                                        .regex(/^(\+49|0049|0)1[5-7][0-9]{6,9}$/, "Bitte gib deine Handynummer im folgenden Format ein: 017612345678."),
                                    contactAddressEmail: z
                                        .string({
                                        required_error: "Bitte gib deine E-Mail Adresse ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine E-Mail Adresse ein.")
                                        .email("Bitte gib eine gültige E-Mail-Adresse ein."),
                                    contactAddressConfirmEmail: z
                                        .string({
                                        required_error: "Bitte gib deine E-Mail Adresse erneut ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine E-Mail Adresse erneut ein.")
                                        .email("Bitte gib eine gültige E-Mail-Adresse ein."),
                                }),
                            },
                        ],
                    },
                    {
                        sections: [
                            {
                                component: "bankAccountSection",
                                defaultValues: {
                                    sepaAccepted: false,
                                },
                                options: {
                                    title: "Zahlung für den Tarif",
                                    label: "Ich ermächtige die Telefónica Germany GmbH & Co. OHG, Zahlungen von meinem Konto per SEPA Lastschrift einzuziehen.",
                                },
                                schema: z.object({
                                    sepaAccepted: z.boolean().refine((value) => value, {
                                        message: "Bitte stimme dem SEPA-Mandat zu",
                                    }),
                                    iban: z
                                        .string({
                                        required_error: "Bitte gib deine IBAN ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib deine IBAN ein."),
                                }),
                            },
                            {
                                component: "deliveryPaymentSection",
                                hide: (options, state, data) => {
                                    return Boolean(!data?.summary?.paymentTypes ||
                                        data?.summary.paymentTypes.length === 0 ||
                                        data?.summary?.paymentTypes?.includes(PaymentType.NoPayment));
                                },
                                defaultValues: {
                                    paymentType: PaymentType.Paypal,
                                },
                                options: {
                                    title: "Zahlung für den Versand",
                                },
                                schema: z.object({
                                    paymentType: z.nativeEnum(PaymentType),
                                }),
                            },
                        ],
                    },
                    {
                        component: "o2.contractSummaryApprovalSection",
                    },
                ],
            },
            {
                type: "contractSummary",
                sections: [
                    {
                        title: "Tarif",
                        sections: [
                            {
                                component: "o2.numberPortabilityInfoSection",
                            },
                            {
                                component: "o2.eSimSection",
                            },
                            {
                                component: "o2.personalCustomerCodeSection",
                                schema: z.object({
                                    personalCustomerCode: z
                                        .string({
                                        required_error: "Bitte gib deine Persönliche Kundenkennzahl ein.",
                                    })
                                        .trim()
                                        .nonempty("Bitte gib eine Persönliche Kundenkennzahl ein.")
                                        .regex(/^[0-9]+$/, "Deine Kundenkennzahl muss aus Zahlen bestehen.")
                                        .length(4, "Bitte gib eine 4-stellige Kundenkennzahl ein."),
                                }),
                            },
                            {
                                component: "identificationSection",
                                options: {
                                    introText: "Für deinen Tarif benötigen wir deine Ausweisdaten zur Weiterleitung an o2.",
                                },
                                defaultValues: {
                                    identificationMode: IdentificationMode.idCard,
                                },
                                schema: z.union([
                                    z.object({
                                        identificationMode: z.literal(IdentificationMode.idCard, {
                                            required_error: "Bitte triff eine Auswahl.",
                                        }),
                                        ...identificationSectionBaseSchema(),
                                        idCardExpirationDate: documentExpirationDateSchema,
                                        idCardNumber: documentNumberSchema,
                                    }),
                                    z.object({
                                        identificationMode: z.literal(IdentificationMode.passport, {
                                            required_error: "Bitte triff eine Auswahl.",
                                        }),
                                        ...identificationSectionBaseSchema(),
                                        passportExpirationDate: documentExpirationDateSchema,
                                        passportNumber: documentNumberSchema.regex(/^[a-zA-Z0-9 ]{6,30}$/, "Bitte gib eine Ausweisnummer an."),
                                    }),
                                ]),
                            },
                            {
                                component: "webIdentSection",
                                options: {
                                    content: (dispatchModal) => {
                                        const { cdnUrl } = extendURLParams();
                                        return (_jsxs(_Fragment, { children: [_jsx(CheckoutSectionText, { children: "Seit dem 01. Juli 2017 fordert der Gesetzgeber vor Aktivierung der SIM-Karte eine Identifizierung des K\u00E4ufers. Hierf\u00FCr bieten wir dir das bequeme Video-Verfahren \u00FCber unseren Partner WebID an." }), _jsx(CheckoutSectionText, { children: "Die Identifikation gelingt per Video-Chat ganz einfach. Du kannst sie gleich im Anschluss an die Bestellung durchf\u00FChren. Das geht von Zuhause und unterwegs. Alles, was du ben\u00F6tigst ist" }), _jsxs("ul", { children: [_jsx("li", { children: "eine Kamera an deinem Smartphone, PC oder Tablet" }), _jsx("li", { children: "deinen Personalausweis oder ein anderes g\u00FCltiges Ausweisdokument mit Lichtbild" })] }), _jsx(CheckoutButton, { role: "dialog", "aria-modal": "true", "aria-label": "weitere Informationen", onClick: (e) => {
                                                        e.preventDefault();
                                                        dispatchModal({
                                                            type: ACTIONS.SET_MODAL,
                                                            payload: {
                                                                type: "generic",
                                                                title: "So funktioniert deine Online-Identifikation",
                                                                content: (_jsxs(_Fragment, { children: [_jsx(Headline, { variant: "h2", children: "So funktioniert deine Online-Identifikation" }), _jsx("p", { children: "Starte den Identifikationsprozess direkt im Anschluss an deine Bestellung \u00FCber den entsprechenden Link auf der Best\u00E4tigungsseite. Du hast gerade keine Zeit oder die ben\u00F6tigte Ausstattung nicht zur Hand? Den f\u00FCr die Identifikation ben\u00F6tigten Link bekommst du zus\u00E4tzlich noch einmal per Mail zugesendet. So kannst du ebenfalls schnell und unkompliziert innerhalb von f\u00FCnf Kalendertagen die Identifizierung vornehmen." }), _jsx(Image, { src: `${cdnUrl}/img/webid/icon-webid-progress.png`, alt: "Fortschritt Web ID" }), _jsx("h3", { children: "Das ben\u00F6tigst du f\u00FCr eine Online-Identifikation." }), _jsxs("ul", { children: [_jsx("li", { children: "Eine funktionsf\u00E4hige Kamera an deinem PC, Tablet oder Smartphone" }), _jsx("li", { children: "Eine stabile Internetverbindung" }), _jsx("li", { children: "Ein g\u00FCltiges Ausweisdokument, wie z. B. Personalausweis oder Reisepass" })] })] })),
                                                            },
                                                        });
                                                    }, children: "weitere Informationen" })] }));
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
            {
                type: "orderSummary",
                showSummaryOnMobile: true,
                submitLabel: "Kostenpflichtig bestellen",
                sections: [
                    {
                        title: "Kontakt & Bezahlung",
                        sections: [
                            {
                                component: "contactAndPaymentOverviewSection",
                                options: {
                                    hideShippingAddress: true,
                                },
                            },
                        ],
                    },
                    {
                        title: "Tarif",
                        sections: [
                            {
                                component: "o2.simOnly.tariffOverviewSection",
                            },
                        ],
                    },
                    {
                        title: "Rechtliche Hinweise",
                        sections: [
                            {
                                component: "legalNoteSection",
                                options: {
                                    legalNote: () => (_jsxs(_Fragment, { children: [_jsx("p", { children: "Mit Klick auf \u201CKostenpflichtig bestellen\u201D erkl\u00E4rst du, dass du" }), _jsxs("ul", { children: [_jsxs("li", { children: ["die", " ", _jsx("a", { href: "/agb/", target: "_blank", rel: "noreferrer", children: "Allgemeinen Gesch\u00E4ftsbedingungen der S-KON eKontor 24 GmbH" })] }), _jsxs("li", { children: ["die", " ", _jsx("a", { href: "/agb-telefonica/", target: "_blank", rel: "noreferrer", children: "Allgemeinen Gesch\u00E4ftsbedingungen der Telef\u00F3nica Germany GmbH & Co. OHG" })] }), _jsxs("li", { children: ["die", " ", _jsx("a", { href: "https://www.webid-solutions.de/de/terms-and-conditions/", target: "_blank", rel: "noreferrer", children: "Allgemeinen Gesch\u00E4ftsbedingungen der WebID Solutions GmbH" })] }), _jsxs("li", { children: ["die", " ", _jsx("a", { href: "https://tarife.mediamarkt.de/widerrufsrecht/", target: "_blank", rel: "noreferrer", children: "Widerrufsbelehrung / das Muster-Widerrufsformular" })] })] }), _jsx("p", { children: "zur Kenntnis genommen hast und mit ihnen einverstanden bist." }), _jsx("p", { children: "Zudem erkl\u00E4rst du deine Einwilligung (hier ansehen) in die Speicherung deiner personenbezogenen Daten durch die WebID Solutions GmbH im Nutzerprofil und in die Verarbeitung der Daten f\u00FCr k\u00FCnftige Identifikationen bzw. digitale Signaturen. Deine Einwilligung kannst du im Videocall oder durch E-Mail an datenschutz@webid-solutions.de widerrufen." }), _jsxs("p", { children: ["Informationen zur Verarbeitung deiner personenbezogenen Daten, insbesondere der Identifizierung \u00FCber die WebID Solutions und zur Bestellabwicklung findest du in den Informationen zum", " ", _jsx("a", { href: "/datenschutz/", target: "_blank", rel: "noreferrer", children: "Datenschutz der S-KON ekontor 24 GmbH" }), " ", "und Informationen zum", " ", _jsx("a", { href: "https://content.ekontor24.net/mediamarkt/pdf/datenschutzmerkblatt-telefonica.pdf", target: "_blank", rel: "noreferrer", children: "Datenschutz von Telef\u00F3nica Germany GmbH & Co. OHG" }), "."] })] })),
                                },
                            },
                            {
                                component: "o2.contractSummarySection",
                            },
                        ],
                    },
                ],
            },
        ],
    },
];
